import React, { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import styled from 'styled-components';
import TeamPerformancePage from './TeamPerformancePage';
import { useAuth } from '../contexts/AuthContext';
import { duration, easing, reducedMotion } from '../styles/motion';
import { pillTrack, pillSegment } from '../styles/buttons'; // [BUTTON-STYLE 2026-09-30]
import { fetchPerformanceTeams, PerfTeamTab } from '../services/api';
import { SkeletonBox } from '../components/Skeleton';
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-23] the team switch's edge fades
import { SlideViewport, SlidePane, slidePos, SLIDE_S } from '../components/ViewSlide'; // [PMPERF-SLIDE 2026-10-08]

/* Unified Performance section: one page with a pill-tab switcher across the
 * combined "Overall" view and each individual team. Each tab renders the shared
 * TeamPerformancePage with the matching team key. Each section filters its month
 * INDEPENDENTLY: switching tabs resets the month back to the current month, so a
 * month picked under one section never leaks into another.
 * [PMPERF-SLIDE 2026-10-08] Each team is now its OWN TeamPerformancePage (keyed by
 * team) so the outgoing report can slide out while the new one slides in (see
 * Content below); a fresh instance starts on the current month, which keeps that reset.
 *
 * The per-team tabs are NOT hardcoded: they come from the server's live PM-team
 * list (discovered from HubSpot), so adding/renaming/deleting a "<Name> - PM"
 * team reshapes this tab bar automatically. "Overall" is always first. */
type Tab = { key: string; label: string; title: string };

const OVERALL_TAB: Tab = { key: 'overall', label: 'Overall', title: 'Overall Performance' };

/* The HubSpot-discovered team list is global (same for everyone) and changes
 * rarely, so we persist it to localStorage. On a hard refresh the in-memory API
 * cache is gone, and without this the tab bar would render the single full-width
 * "Overall" pill for the ~330ms fetch round-trip before snapping to the real
 * tabs. Reading the last-known layout lets the bar render the correct number of
 * tabs instantly (stale-while-revalidate: the live fetch still corrects it). */
const TEAM_TABS_CACHE_KEY = 'perf.teamTabs.v1';
// Cold-start (no cache ever written) placeholder count — Overall + ~4 teams is
// the current shape; only ever used on a user's very first visit.
const PLACEHOLDER_TAB_COUNT = 5;

function readCachedTeamTabs(): PerfTeamTab[] {
  try {
    const raw = localStorage.getItem(TEAM_TABS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is PerfTeamTab =>
        !!t && typeof t.key === 'string' && typeof t.label === 'string' && typeof t.title === 'string'
    );
  } catch {
    return [];
  }
}

function writeCachedTeamTabs(tabs: PerfTeamTab[]) {
  try {
    localStorage.setItem(TEAM_TABS_CACHE_KEY, JSON.stringify(tabs));
  } catch {
    /* ignore quota / private-mode write failures — the live fetch still works */
  }
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  min-width: 0;
  background: ${(p) => p.theme.surface};
  /* Clear the floating bottom dock so the last row sits above the pill
     instead of hiding behind it. */
  padding-bottom: 88px;
`;

/* Soft glass pill bar — same recipe as the app's other glass strips. The
 * vertical margin gives the bar room to breathe from the toolbar below it.
 * [BUTTON-STYLE 2026-09-30] Restyled as the app's one switch pill: the same track
 * (pillTrack size, the Stats / Dialing / Refunds pill colours), 30px segments and the
 * same green thumb. Layout (full width, horizontal scroll, margins) is unchanged. */
const TabsBar = styled.div`
  position: relative; /* anchors the sliding indicator */
  display: flex;
  ${pillTrack}
  margin: 16px 20px;
  /* [RESPONSIVE 2026-06-23] With many team tabs on a narrow screen the pills used
     to squash their PM-name labels to an ellipsis. Tabs now keep a readable floor
     (see Tab) and the bar scrolls horizontally instead; the active tab is kept in
     view and the indicator is measured from the real tab box (see below). Scrollbar
     hidden to match the app's other glass strips. */
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar { width: 0; height: 0; display: none; }
  /* [LIQUID-GLASS 2026-11-02, user] the track pressed into the glass; the indicator glossy, sliding the theme switch's way */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border: 1px solid transparent;
  @media (max-width: 600px) {
    margin: 12px;
  }

  /* [PHONE-LAYOUT 2026-10-23] Phones: the end(s) with more teams past them fade out (data-fade = left / right / both, set
     by the page from the bar's scroll position), so the teams beyond the edge read as "swipe for more" — the track's
     round end used to look complete with Noora, ROW, Shreyansh and Sulabh hidden past it. The dock's own phone cue. */
  @media (max-width: 768px) {
    &[data-fade='right'] {
      -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 36px), transparent);
      mask-image: linear-gradient(to right, #000 calc(100% - 36px), transparent);
    }
    &[data-fade='left'] {
      -webkit-mask-image: linear-gradient(to left, #000 calc(100% - 36px), transparent);
      mask-image: linear-gradient(to left, #000 calc(100% - 36px), transparent);
    }
    &[data-fade='both'] {
      -webkit-mask-image: linear-gradient(to right, transparent, #000 36px, #000 calc(100% - 36px), transparent);
      mask-image: linear-gradient(to right, transparent, #000 36px, #000 calc(100% - 36px), transparent);
    }
  }
`;

/* Cold-start skeleton of the bar — same glass strip, but holding shimmer pills
 * instead of the real tabs (and no green indicator) so a first-ever load never
 * flashes a single full-width "Overall" pill. Adds a gap the real bar doesn't
 * need (its pills butt against each other under the sliding indicator). */
const SkTabsBar = styled(TabsBar)`
  gap: 8px;
`;

/* The single highlighted pill. It slides between tabs via translateX (compositor
 * only — never animates width/left) using the app's emphasized curve, so the
 * switch reads with the same smoothness as the theme toggle. */
/* [RESPONSIVE 2026-06-23] Position + width are now measured from the active tab's
 * real box (driven by inline style) instead of assuming equal-width tabs that fill
 * the bar — so the highlight stays aligned even when tabs differ in width or the
 * bar scrolls. Both transform and width animate so it slides smoothly between
 * differently sized pills. */
const Indicator = styled.div`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), linear-gradient(180deg, ${(p) => p.theme.green600} 0%, ${(p) => p.theme.green700} 100%);
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.32)' : 'rgba(20, 107, 84, 0.32)')}, inset 0 1px 0 rgba(255,255,255,0.45);
  transition: transform 0.42s cubic-bezier(0.32, 0.72, 0, 1), width 0.42s cubic-bezier(0.32, 0.72, 0, 1); /* [LIQUID-GLASS] the theme switch's slide */
  z-index: 0;

  ${reducedMotion} {
    transition: none;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1; /* sit above the sliding pill */
  /* [RESPONSIVE 2026-06-23] grow to fill equally when there's room, but never
     shrink below a readable floor — the bar scrolls instead of ellipsizing names. */
  flex: 1 0 110px;
  min-width: 110px;
  ${pillSegment} /* [BUTTON-STYLE 2026-09-30] the one segment size (was 42px tall, 14px text) */
  border: none;
  background: transparent;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => (p.$active ? '#fff' : p.theme.muted)};
  transition: color ${duration.base} ${easing.standard};

  &:hover {
    color: ${(p) => (p.$active ? '#fff' : p.theme.greenInk)}; /* [GREEN-ON-DARK 2026-08-27] */
  }
  &:active {
    transform: translateY(1px);
  }

  /* [MOBILE 2026-07-07] Give the pill room for the stacked two-line team label
     (see TabLabel) — "Team -" over the name — so long names no longer ellipsize to
     "Team - No…" in the narrow, scrollable bar. Centres single-line tabs too. */
  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: auto;
    min-height: 46px;
    padding: 4px 12px;
    line-height: 1.15;
    white-space: normal;
  }
`;

/* [MOBILE 2026-07-07] Team-tab label wrapper. Inline on desktop ("Team - Ilma",
 * one line — identical to the old plain-text label); stacked on phones ("Team -"
 * over the name) so the name stays fully visible in the narrow, scrollable pill
 * instead of ellipsizing to "Team - No…". Only team tabs use this — "Overall"
 * (and any non-"Team - X" label) renders as plain text (see renderTabLabel). */
const TabLabel = styled.span`
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  vertical-align: middle;
  gap: 5px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;

  .lead { flex-shrink: 0; }
  .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1px;
    line-height: 1.12;
  }
`;

/* "Team - <Name>" → a two-part label the pill can stack on phones. Any label that
 * isn't a team tab (e.g. "Overall") is returned unchanged. The accessible name is
 * preserved separately via aria-label on the Tab, so the visual split never
 * affects what a screen reader announces. */
const TEAM_LABEL_RE = /^(Team\s*-)\s*(.+)$/;
function renderTabLabel(label: string): React.ReactNode {
  const m = TEAM_LABEL_RE.exec(label);
  if (!m) return label;
  return (
    <TabLabel>
      <span className="lead">{m[1]}</span>
      <span className="name">{m[2]}</span>
    </TabLabel>
  );
}

/* [PMPERF-SLIDE 2026-10-08, user] Was the tab-change "dim instantly, ease back" wrapper; with the report's
 * skeleton it made the figures blink on every team switch. Now the app's shared slide viewport
 * (components/ViewSlide: clip on both axes, never hidden); flex and min-width kept from the old wrapper. */
const Content = styled(SlideViewport)`
  flex: 1;
  min-width: 0;
`;

/* [PMPERF-SLIDE 2026-10-08, user] Picking a team SLIDES the report below the pill bar, like the app's other
 * view toggles (shared ViewSlide recipe: 0.42 s, emphasized curve, instant under reduced motion). A team to
 * the RIGHT of the one on show: the current report leaves to the left while the new team's comes in from
 * the right; a team to the LEFT: the reverse. The pill bar never moves.
 * [PMPERF-QUICK 2026-10-08, user] "the green colour moves but the page moves 2-3 seconds later — it should be
 * quick": the new team no longer waits off-screen for its figures (that wait, plus drawing the report, was the
 * delay). It mounts at its side and slides in TWO FRAMES after the click — just long enough for the browser to
 * place it there first — then fills in where it lands (skeleton, then its figures fade in, or at once when
 * cached). The report that left is dropped once its slide is over. */
const SLIDE_MS = parseFloat(SLIDE_S) * 1000;
/* [PMPERF-QUICK 2026-10-08] Memoised: the slide's own state changes three times per switch, and re-rendering
 * every heavy report on screen for each of them is what made the start of the slide sluggish. teamKey and
 * title are plain strings, so a report re-renders only when its own data changes. */
const TeamReport = React.memo(TeamPerformancePage);
type SlideState = {
  shown: string | null; // team whose report is on show (in flow)
  pending: string | null; // team loading hidden off to its side, not slid in yet
  leaving: { key: string; until: number }[]; // reports sliding out, dropped once `until` has passed
};

const PerformancePage: React.FC = () => {
  const { user } = useAuth();

  // Live team tabs from the server (HubSpot-discovered). Seed from the
  // localStorage cache so a hard refresh renders the correct tab count
  // immediately; the live fetch below refreshes (and re-caches) it.
  const [teamTabs, setTeamTabs] = useState<PerfTeamTab[]>(readCachedTeamTabs);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchPerformanceTeams()
      .then((t) => { if (alive) { setTeamTabs(t); writeCachedTeamTabs(t); } })
      .catch(() => { /* keep cached / Overall-only on failure */ })
      .finally(() => { if (alive) setTeamsLoaded(true); });
    return () => { alive = false; };
  }, []);

  const allTabs = useMemo<Tab[]>(() => [OVERALL_TAB, ...teamTabs], [teamTabs]);

  // Scope the tabs to what the signed-in user may see:
  //   - Admins & seniors    → every tab (Overall + all teams) — they arrive with an
  //                           empty performance_teams and fall through to all tabs.
  //   - Team members        → only the tab for the team they belong to (owner and
  //                           regular member alike, e.g. Team Shreyansh → Shreyansh).
  //   - Non-PM grantees     → all tabs (legacy: someone granted the section but not
  //                           tied to any team, e.g. finance — empty performance_teams).
  const tabs = useMemo(() => {
    if (!user || user.is_admin) return allTabs;
    const owned = user.performance_teams || [];
    if (owned.length === 0) return allTabs;
    const scoped = allTabs.filter((t) => owned.includes(t.key));
    return scoped.length ? scoped : allTabs;
  }, [user, allTabs]);

  const [active, setActive] = useState<string>(OVERALL_TAB.key);
  let index = tabs.findIndex((t) => t.key === active);
  if (index < 0) index = 0;
  const current = tabs[index];

  // [RESPONSIVE 2026-06-23] Measure the active tab's real box so the sliding
  // indicator stays aligned regardless of tab width / horizontal scroll, and keep
  // the active tab scrolled into view when the bar overflows on narrow screens.
  const barRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [ind, setInd] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const syncIndicator = () => {
      const el = tabRefs.current[index];
      const bar = barRef.current;
      if (!el || !bar) return;
      setInd({ left: el.offsetLeft, width: el.offsetWidth });
      // Scroll only the bar (not the page) so the active pill is visible.
      const target = el.offsetLeft - (bar.clientWidth - el.offsetWidth) / 2;
      bar.scrollLeft = Math.max(0, target);
    };
    syncIndicator();
    // The bar is overflow-x:auto so its border box doesn't grow with tab content;
    // a font swap reflows the tabs without firing the bar's ResizeObserver. Re-sync
    // once 'Plus Jakarta Sans' settles so the highlight isn't pinned to the
    // fallback-font measurement (mirrors BottomNav's guard).
    const fonts = (document as any).fonts;
    if (fonts?.ready) fonts.ready.then(syncIndicator).catch(() => {});
    const ro = new ResizeObserver(syncIndicator);
    if (barRef.current) ro.observe(barRef.current);
    return () => ro.disconnect();
  }, [index, tabs]);

  const select = useCallback(
    (key: string) => {
      if (key === active) return;
      setActive(key); // [PMPERF-SLIDE 2026-10-08] no dim any more: the report slides (see below)
    },
    [active]
  );
  // [FLUID-8 2026-10-12, user] A team tab switches on the mouse PRESS, as browser tabs do; touch / keyboard click.
  const tabPressedRef = useRef(false);

  // A scoped team owner (not admin, owns ≥1 team) can't view "Overall", so don't
  // render the default Overall tab in the brief window before the live team list
  // arrives — wait so we land straight on their own team (avoids a 403 flash).
  const isScopedOwner = !!user && !user.is_admin && (user.performance_teams || []).length > 0;
  const contentReady = !isScopedOwner || teamTabs.length > 0;

  // Only a genuine first-ever visit (no cached layout AND the fetch hasn't
  // returned) shows the skeleton bar — every refresh after that is seeded from
  // the cache and renders the real tabs straight away.
  const showSkeletonTabs = !teamsLoaded && teamTabs.length === 0;

  // [PHONE-LAYOUT 2026-10-23] Phones only: mark which end(s) of the team switch have more teams past them (data-fade =
  // left / right / both) for the edge fades in TabsBar's phone CSS; re-read on every scroll (a swipe, or the active team
  // brought into view), resize and font load. Never set on a laptop (the attribute is removed there).
  const isMobile = useIsMobile();
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    if (!isMobile) { bar.removeAttribute('data-fade'); return undefined; }
    const update = () => {
      const max = bar.scrollWidth - bar.clientWidth;
      const left = bar.scrollLeft > 2;
      const right = bar.scrollLeft < max - 2;
      const v = left && right ? 'both' : left ? 'left' : right ? 'right' : '';
      if (v) bar.setAttribute('data-fade', v); else bar.removeAttribute('data-fade');
    };
    update();
    bar.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const fonts = (document as any).fonts;
    if (fonts?.ready) fonts.ready.then(update).catch(() => {});
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(bar);
    return () => {
      bar.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [isMobile, tabs, showSkeletonTabs]);

  // [PMPERF-SLIDE 2026-10-08] Which team's report is on show / loading off-screen / sliding out.
  const targetKey = contentReady ? current.key : null;
  const [slide, setSlide] = useState<SlideState>({ shown: null, pending: null, leaving: [] });
  const viewportRef = useRef<HTMLDivElement>(null);
  const order = useMemo(() => tabs.map((t) => t.key), [tabs]);
  // Last-known title per team, so a report sliding out keeps its heading even if its team just left the list.
  const titlesRef = useRef(new Map<string, string>());
  for (const t of allTabs) titlesRef.current.set(t.key, t.title);

  useEffect(() => {
    if (!targetKey) return;
    // [FLUID-8 2026-10-12] A transition: the new team's report (a heavy mount, ~70 ms) builds in slices, so the green pill
    // keeps its frames as it slides instead of freezing until the report is built. The two-frame start below is unchanged.
    startTransition(() => setSlide((s) => {
      if (!s.shown) return { ...s, shown: targetKey }; // the page's first report: no slide, its skeleton shows
      if (targetKey === s.shown) return s.pending ? { ...s, pending: null } : s; // back to the team on show
      if (s.leaving.some((l) => l.key === targetKey)) {
        // Still sliding out with its figures intact: bring it straight back.
        const leaving = s.leaving.filter((l) => l.key !== targetKey);
        return { shown: targetKey, pending: null, leaving: [...leaving, { key: s.shown, until: Date.now() + SLIDE_MS }] };
      }
      return s.pending === targetKey ? s : { ...s, pending: targetKey };
    }));
  }, [targetKey]);

  // [PMPERF-QUICK 2026-10-08] Two frames after the click (see below): slide the new team in, the current one out.
  const finishPending = useCallback((key: string) => {
    // Make the browser take note of the waiting pane's off-to-the-side spot first, so the move to the centre
    // animates even when its figures came straight from the cache in the same frame the pane was added.
    void viewportRef.current?.offsetHeight;
    setSlide((s) => (s.pending !== key || !s.shown ? s : {
      shown: key,
      pending: null,
      leaving: [...s.leaving, { key: s.shown, until: Date.now() + SLIDE_MS }],
    }));
  }, []);

  useEffect(() => {
    if (!slide.pending) return;
    const key = slide.pending;
    // [PMPERF-QUICK 2026-10-08] Two frames, not "when its figures are drawn / after 1 s": frame 1 paints the new
    // team at its side, frame 2 starts the slide — the page now moves with the green pill.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => finishPending(key)); });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [slide.pending, finishPending]);

  // Drop each report once its slide-out is over.
  useEffect(() => {
    if (!slide.leaving.length) return;
    const due = Math.min(...slide.leaving.map((l) => l.until));
    const t = window.setTimeout(() => {
      const now = Date.now();
      setSlide((s) => {
        const left = s.leaving.filter((l) => l.until > now);
        return left.length === s.leaving.length ? s : { ...s, leaving: left };
      });
    }, Math.max(0, due - Date.now()) + 60);
    return () => window.clearTimeout(t);
  }, [slide.leaving]);

  const shownKey = slide.shown ?? targetKey;
  // Panes in TAB order, so React only ever adds or removes one and never moves it (a moved node restarts its slide).
  const paneKeys = shownKey
    ? [...new Set([shownKey, ...slide.leaving.map((l) => l.key), ...(slide.pending ? [slide.pending] : [])])]
        .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    : [];

  return (
    <Wrap>
      {showSkeletonTabs ? (
        <SkTabsBar aria-busy="true" aria-label="Loading performance tabs">
          {Array.from({ length: PLACEHOLDER_TAB_COUNT }).map((_, i) => (
            <SkeletonBox key={i} $h="30px" $radius="999px" style={{ flex: 1 }} />
          ))}
        </SkTabsBar>
      ) : (
        <TabsBar ref={barRef} role="tablist" aria-label="Performance teams">
          <Indicator aria-hidden style={{ transform: `translateX(${ind.left}px)`, width: ind.width }} />
          {tabs.map((t, i) => (
            <Tab
              key={t.key}
              ref={(el) => { tabRefs.current[i] = el; }}
              role="tab"
              aria-selected={active === t.key}
              aria-label={t.label}
              $active={active === t.key}
              onPointerDown={(e) => {
                tabPressedRef.current = isPlainMousePress(e);
                if (tabPressedRef.current) select(t.key);
              }}
              onClick={(e) => {
                if (clickEndsPress(tabPressedRef, e)) return; // the press already switched
                select(t.key);
              }}
            >
              {renderTabLabel(t.label)}
            </Tab>
          ))}
        </TabsBar>
      )}

      {/* [PMPERF-SLIDE 2026-10-08] One keyed report per team on screen or in motion (see Content above): the
          one on show, a new team placed at its side for two frames, and any sliding out.
          [PMPERF-QUICK 2026-10-08] No onReady wait any more; the memoised TeamReport, so the slide's own state
          changes don't re-render the reports. */}
      <Content ref={viewportRef}>
        {contentReady && shownKey && paneKeys.map((key) => {
          const pos = slidePos(shownKey, key, order);
          return (
            <SlidePane key={key} $pos={pos} aria-hidden={pos !== 'center'} data-perf-pane={key}>
              <TeamReport teamKey={key} title={titlesRef.current.get(key) ?? current.title} />
            </SlidePane>
          );
        })}
      </Content>
    </Wrap>
  );
};

export default PerformancePage;
